/** @jsxImportSource @emotion/react */
import React from 'react';
import { Card, Space, Tag, Typography } from 'antd';
import { Trans, useTranslation } from 'react-i18next';

const { Text } = Typography;

interface Props {
  harvestForecast: any;
  isForecastAnnualLike: boolean;
  harvestGrossTargetKg: number | null;
  harvestMarketableTargetKg: number | null;
  plantingQuantity: number;
}

/**
 * На культуре с квантом плода "Доступно к сбору" округлено вниз до целых
 * товарных плодов, поэтому оно стоит на месте по несколько дней подряд, пока
 * растение исправно копит — со стороны это выглядит как замерший счётчик.
 * Считаем остаток сверх целых плодов и оценку срока до следующей ступеньки.
 */
const resolveNextFruitProgress = (harvestForecast: any) => {
  const unitKg = Number(harvestForecast?.fruitUnitWeightKg);
  const ripeKg = Number(harvestForecast?.ripeKg);
  const availableKg = Number(harvestForecast?.availableKg || 0);
  if (!Number.isFinite(unitKg) || unitKg <= 0) return null;
  if (!Number.isFinite(ripeKg) || ripeKg <= 0) return null;

  const remainderKg = ripeKg - availableKg;
  // Хвост меньше грамма — показывать нечего; на сборе целыми плодами остатка нет.
  if (remainderKg <= 0.001) return null;

  const toNextFruitKg = unitKg - remainderKg;
  if (toNextFruitKg <= 0) return null;

  // Оценка по темпу завязи: это скорость поступления массы, а не дозревания,
  // поэтому срок приблизительный и подписан как таковой.
  const dailyKg = Number(harvestForecast?.dailyPotentialKg);
  const etaDays = Number.isFinite(dailyKg) && dailyKg > 0
    ? Math.ceil(toNextFruitKg / dailyKg)
    : null;

  return { remainderKg, toNextFruitKg, etaDays };
};

const HarvestForecastCard: React.FC<Props> = ({
  harvestForecast,
  isForecastAnnualLike,
  harvestGrossTargetKg,
  harvestMarketableTargetKg,
  plantingQuantity,
}) => {
  const { t } = useTranslation('plantings');
  if (!harvestForecast?.isEnabled && !isForecastAnnualLike) return null;
  const b = { b: <Text strong /> };
  const kg2 = (value: unknown) => Number(value || 0).toFixed(2);

  const isLeafCut = harvestForecast?.harvestType === 'leaf_cut';
  const population = harvestForecast?.fruitSummary;
  const countBasis = population ? Number(harvestForecast.fruitReferencePlantCount) : plantingQuantity;
  const nextFruit = isLeafCut || population ? null : resolveNextFruitProgress(harvestForecast);

  return (
    <Card size="small" title={population ? t('harvestForecast.titleFruitState') : t('harvestForecast.titleForecast')}>
      <Space direction="vertical" size={4} css={{ width: '100%' }}>
        {!harvestForecast?.isEnabled && (
          <Text type="secondary">
            {t('harvestForecast.noModel')}
          </Text>
        )}
        {harvestForecast?.isEnabled && harvestForecast?.isDefaultModel && (
          <Text type="warning">
            {t('harvestForecast.defaultModel')}
          </Text>
        )}
        {harvestForecast?.isEnabled && (
          <>
            {isLeafCut && (
              <Text>{t('harvestForecast.leafCutAvailable', { grams: Math.round(Number(harvestForecast.leafAvailableKg || 0) * 1000) })}
                {' · '}{t('harvestForecast.leafRegrowth', { pct: Math.round(Number(harvestForecast.leafRegrowthPct || 0)) })}
                {' · '}{t('harvestForecast.cutCount', { count: Number(harvestForecast.cutCount || 0) })}
                {Number(harvestForecast.daysUntilNextCut) > 0 && ` · ${t('harvestForecast.daysUntilNextCut', { count: Number(harvestForecast.daysUntilNextCut) })}`}
              </Text>
            )}
            {!population && <Space wrap size={[8, 4]}>
              <Tag color={harvestForecast.ready ? 'green' : (Number(harvestForecast.readinessPct || 0) >= 100 ? 'red' : 'orange')}>
                {harvestForecast.ready
                  ? t('harvestForecast.windowOpen')
                  : (Number(harvestForecast.readinessPct || 0) >= 100
                    ? t('harvestForecast.windowClosed')
                    : t('harvestForecast.preparing'))}
              </Tag>
              <Text type="secondary">{t('harvestForecast.readiness', { pct: Number(harvestForecast.readinessPct || 0).toFixed(0) })}</Text>
              {!isLeafCut && harvestForecast.peakFruitingEdu != null && (
                <Text type="secondary">{t('harvestForecast.peakFruiting', { value: Math.round(harvestForecast.peakFruitingEdu) })}</Text>
              )}
              {harvestForecast.completeLifecycleOnHarvest && (
                <Text type="warning">{t('harvestForecast.harvestEndsLifecycle')}</Text>
              )}
            </Space>}

            <Text type="secondary">
              {population
                ? t('harvestForecast.populationNote')
                : t('harvestForecast.phaseModelNote')}
            </Text>

            {harvestGrossTargetKg != null && (
              <Text type="secondary">
                <Trans t={t} i18nKey="harvestForecast.seasonTarget" values={{ value: harvestGrossTargetKg.toFixed(2) }} components={b} />
                {harvestMarketableTargetKg != null && (
                  <Trans t={t} i18nKey="harvestForecast.seasonTargetMarketable" values={{ value: harvestMarketableTargetKg.toFixed(2) }} components={b} />
                )}
              </Text>
            )}

            {population && <Space wrap size={[12, 6]}>
              <Text><Trans t={t} i18nKey="harvestForecast.budsAndFlowers" values={{ buds: population.budCount, flowers: population.openFlowerCount }} components={b} /></Text>
              {(['green', 'turning', 'ripe', 'overripe'] as const).map((key) => (
                <Text key={key}>
                  <Trans
                    t={t}
                    i18nKey="harvestForecast.fruitStage"
                    values={{ label: t(`harvestForecast.stage.${key}`), count: population.stages[key].count, kg: Number(population.stages[key].massKg).toFixed(2) }}
                    components={b}
                  />
                </Text>
              ))}
              <Text><Trans t={t} i18nKey="harvestForecast.onPlants" values={{ kg: kg2(population.totalMassKg) }} components={b} /></Text>
            </Space>}

            <Space wrap size={[12, 6]}>
              <Text><Trans t={t} i18nKey="harvestForecast.perDay" values={{ value: Number(harvestForecast.dailyPotentialKg || 0).toFixed(3) }} components={b} /></Text>
              <Text><Trans t={t} i18nKey="harvestForecast.formedTotal" values={{ value: kg2(harvestForecast.cumulativePotentialKg) }} components={b} /></Text>
              <Text><Trans t={t} i18nKey="harvestForecast.available" values={{ value: kg2(harvestForecast.availableKg) }} components={b} /></Text>
              <Text><Trans t={t} i18nKey="harvestForecast.collected" values={{ value: kg2(harvestForecast.collectedCumulativeKg) }} components={b} /></Text>
              <Text><Trans t={t} i18nKey="harvestForecast.lost" values={{ value: kg2(harvestForecast.lostCumulativeKg) }} components={b} /></Text>
              <Text>
                <Trans
                  t={t}
                  i18nKey={population ? 'harvestForecast.collectedPlusStanding' : 'harvestForecast.projectedTotal'}
                  values={{ value: kg2(harvestForecast.projectedTotalKg) }}
                  components={b}
                />
              </Text>
            </Space>

            {population && <Text type="secondary">{t('harvestForecast.collectedPlusStandingNote')}</Text>}

            {nextFruit && (
              <Text type="secondary">
                <Trans
                  t={t}
                  i18nKey={nextFruit.etaDays != null ? 'harvestForecast.nextFruitWithEta' : 'harvestForecast.nextFruit'}
                  values={{
                    unit: Number(harvestForecast.fruitUnitWeightKg).toFixed(2),
                    remainder: nextFruit.remainderKg.toFixed(2),
                    toNext: nextFruit.toNextFruitKg.toFixed(2),
                    ...(nextFruit.etaDays != null ? { count: nextFruit.etaDays } : {}),
                  }}
                  components={b}
                />
              </Text>
            )}

            {plantingQuantity > 1 && (
              <Space wrap size={[12, 6]}>
                <Text><Trans t={t} i18nKey="harvestForecast.perDayBatch" values={{ value: kg2(Number(harvestForecast.dailyPotentialKg || 0) * countBasis) }} components={b} /></Text>
                <Text>
                  <Trans
                    t={t}
                    i18nKey={population ? 'harvestForecast.collectedPlusStandingBatch' : 'harvestForecast.projectedTotalBatch'}
                    values={{ value: kg2(Number(harvestForecast.projectedTotalKg || 0) * countBasis) }}
                    components={b}
                  />
                </Text>
              </Space>
            )}
          </>
        )}
      </Space>
    </Card>
  );
};

export default HarvestForecastCard;
